import React from "react";
//#region types
import { RouteInfo } from "models/utilTypes/routeTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Settings } from "components/settings";
import { CreateView, SupportCaseReport } from "components/support";
import { IncomingInvoiceReport, IncomingInvoiceView } from "components/incomingInvoice";
import Login from "components/login/login";
import { CustomerProfile, CustomerReport, CustomerSettings } from "components/customer";
import {
  CreateInvoiceContainer,
  CustomerInvoicesReport,
  InvoiceView,
  RecurringInvoicesReport,
  RecurringInvoiceView,
} from "components/invoice";
import { SalaryDeviationReport } from "components/salaryDeviation";
import { CommissionSalaryReport } from "components/commissionSalary";
import { ReceiptReport } from "components/receipt";
import { SuppliersReport, SupplierSettings, SupplierProfile } from "components/supplier";
import { ReceiptView } from "components/receipt";
import { MainReport } from "components/reports";
import { ArticleProfileDashboard, ArticleSettings, ArticlesReport } from "components/article";
import { DocumentDraftReport, DocumentDraftView } from "components/documents";
import { AllToDoReport } from "components/todos";
import { AllCompaniesView } from "components/allCompanies";
import { Home } from "components/home";
import { BalanceReport, CompanyBooksReport, ReconciliationReportView, ResultReport } from "components/accounting";
import { GrossSalaryReport, SalaryReport, SalarySpecificationReport } from "components/salary";
import {
  GenericDocumentsReport,
  SharedDocumentReport,
  SIEFilesReport,
  GenericDocumentView,
} from "components/documents";
import { EmployeeSettings } from "components/settings";
import { CashReconciliationReport, CashReconciliationView } from "components/cashReconciliation";
import { RevenueReportView } from "components/revenueReporting";
import {
  BusinessUnitsReport,
  CreateBusinessUnit,
  BusinessUnitProfile,
  BusinessUnitSettings,
} from "components/businessUnit";
import {
  TimeReportingProjectsReport,
  CreateTimeReportingProject,
  TimeReportingProjectProfile,
  TimeReportingProjectSettings,
} from "components/timeReportingProject";
import { QuotesReport } from "components/quotation";
import { PaymentMethodProfile } from "components/receipt";
import { CreateQuotationContainer, QuotationView } from "components/quotation";
import { AgreementsReport } from "components/agreement";
import { TimeReportingMonthView, TimeReportingView } from "components/timeReporting";
import { SurveyView } from "components/survey";
import { VoucherView } from "components/voucher";
import { FinancialStatementReport, FinancialStatementView } from "components/annualAccount";
import CreateCustomerView from "components/customer/createCustomerView";
import { QualifiedShareDividendReport, QualifiedShareDividendView } from "components/qualifiedShareDividend";
import { EditSalaries } from "components/salary";
import TodoView from "components/todos/view";
import { EditBoardMembers } from "components/boardMember";
import {
  AdvisorDashboard,
  EmailAddresses as AdvisorEmailAddresses,
  AssetsReportView,
  HoldingRegisterReportView,
  MonthlyReportDetailView,
  MonthlyReportsList,
  VatReportDetailView,
  VatReportsList,
} from "components/advisor";
import SupportCaseView from "components/support/view";
import { TeamPage } from "components/support/team";
import { SalaryDraftView } from "components/salaryDraft";
import { DocumentationLogView } from "components/advisor/documentationLog";
import { ExportedFilesView } from "components/exportedFiles";
import { DeadlineReport } from "components/deadline";
import { CreateSelfInvoiceViewContainer } from "components/selfInvoice";
import { AutomationRuleReport } from "components/automationRule";
import { WintDeviationReport, WintSalaryReport } from "components/wintSalary";
import { BenefitReport } from "components/benefits";
import { MonthlyReportView } from "components/monthlyReport";
//#endregion

export const RouteConfig: Array<RouteInfo> = [
  {
    path: "",
    element: React.createElement(Home),
    exact: true,
    displayText: TranslationKeys.Common.Dashboard,
  },
  {
    path: "AllCompanies",
    element: React.createElement(AllCompaniesView),
    displayText: TranslationKeys.Todos.AllCompanies,
  },
  {
    path: "Reports",
    displayText: TranslationKeys.Common.Reports,
    children: [
      {
        path: "Articles",
        element: React.createElement(ArticlesReport),
        displayText: TranslationKeys.Common.Articles,
      },
      {
        path: "Revenue",
        displayText: TranslationKeys.Common.Revenue,
        disableBackNavigation: true,
        children: [
          {
            path: "Invoices",
            element: React.createElement(CustomerInvoicesReport),
            displayText: TranslationKeys.Invoice.CustomerInvoices,
          },
          {
            path: "RecurringInvoices",
            element: React.createElement(RecurringInvoicesReport),
            displayText: TranslationKeys.Invoice.RecurringInvoices,
          },
          {
            path: "CashReconciliations",
            element: React.createElement(CashReconciliationReport),
            displayText: TranslationKeys.CashReconciliation.CashReconciliations,
          },
          {
            path: "Projects",
            element: React.createElement(TimeReportingProjectsReport),
            displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
          },
        ],
      },
      {
        path: "Expenses",
        displayText: TranslationKeys.Common.Expenses,
        disableBackNavigation: true,
        children: [
          {
            path: "IncomingInvoices",
            element: React.createElement(IncomingInvoiceReport),
            displayText: TranslationKeys.Reports.IncomingInvoices,
          },
          {
            path: "Receipts",
            element: React.createElement(ReceiptReport),
            displayText: TranslationKeys.Reports.Receipts,
          },
        ],
      },
      {
        path: "DocumentDrafts",
        element: React.createElement(DocumentDraftReport),
        displayText: TranslationKeys.DocumentDraft.ReceivedDocuments,
      },
      {
        path: "Customers",
        element: React.createElement(CustomerReport),
        displayText: TranslationKeys.Customer.Customers,
      },
      {
        path: "Quotes",
        element: React.createElement(QuotesReport),
        displayText: TranslationKeys.Invoice.Quotes,
      },
      {
        path: "IncomingInvoices",
        element: React.createElement(IncomingInvoiceReport),
        displayText: TranslationKeys.IncomingInvoice.IncomingInvoices,
      },
      {
        path: "Suppliers",
        element: React.createElement(SuppliersReport),
        displayText: TranslationKeys.Supplier.Supplier,
      },
      {
        path: "AllToDos",
        element: React.createElement(AllToDoReport),
        displayText: TranslationKeys.Common.Todo,
      },
      {
        path: "Board",
        element: React.createElement(EditBoardMembers),
        displayText: TranslationKeys.Reports.Board,
      },
      {
        path: "IncomeStatement",
        element: React.createElement(ResultReport),
        displayText: TranslationKeys.Accounting.IncomeStatement,
      },
      {
        path: "BalanceSheet",
        element: React.createElement(BalanceReport),
        displayText: TranslationKeys.Accounting.BalanceSheet,
      },
      {
        path: "SharedDocuments",
        element: React.createElement(SharedDocumentReport),
        displayText: TranslationKeys.Document.SharedDocuments,
      },
      {
        path: "SIE",
        element: React.createElement(SIEFilesReport),
        displayText: TranslationKeys.Document.SIEFiles,
      },
      {
        path: "AccountingDocuments",
        element: React.createElement(GenericDocumentsReport),
        displayText: TranslationKeys.Document.GenericDocuments,
      },
      {
        path: "BusinessUnits",
        element: React.createElement(BusinessUnitsReport),
        displayText: TranslationKeys.BusinessUnit.BusinessUnits,
      },
      {
        path: "Projects",
        element: React.createElement(TimeReportingProjectsReport),
        displayText: TranslationKeys.TimeReporting.Projects,
      },
      {
        path: "Accounting",
        element: React.createElement(CompanyBooksReport),
        displayText: TranslationKeys.Common.Accounting,
      },
      {
        path: "Agreements",
        element: React.createElement(AgreementsReport),
        displayText: TranslationKeys.Common.Agreement,
      },
      {
        path: "Automations",
        element: React.createElement(AutomationRuleReport),
        displayText: TranslationKeys.Common.Automation,
      },
      {
        path: "FinancialStatements",
        element: React.createElement(FinancialStatementReport),
        displayText: TranslationKeys.Common.FinancialStatements,
      },
      {
        path: "QualifiedShareDividend",
        element: React.createElement(QualifiedShareDividendReport),
        displayText: TranslationKeys.Common.K10Forms,
      },
      {
        path: "ReconciliationReport",
        element: React.createElement(ReconciliationReportView),
        displayText: TranslationKeys.Accounting.Reconciliation,
      },
      {
        path: "DeadlineReport",
        element: React.createElement(DeadlineReport),
        displayText: TranslationKeys.Common.Deadline,
      },
      {
        path: "HoldingRegisters",
        displayText: TranslationKeys.HoldingRegister.HoldingRegister,
        element: React.createElement(HoldingRegisterReportView),
      },
      {
        path: "HoldingRegister",
        displayText: TranslationKeys.HoldingRegister.HoldingRegister,
        backNavigationPath: "Reports/HoldingRegisters",
        children: [
          {
            path: ":id",
            element: React.createElement(AssetsReportView),
            displayText: TranslationKeys.HoldingRegister.HoldingRegister,
          },
        ],
      },
      {
        path: "ExportedFilesView",
        element: React.createElement(ExportedFilesView),
        displayText: TranslationKeys.Common.ExportedFiles,
      },
      {
        path: "",
        element: React.createElement(MainReport),
        displayText: "",
      },
    ],
  },
  {
    path: "Support",
    displayText: TranslationKeys.Common.Support,
    children: [
      {
        path: "",
        element: React.createElement(SupportCaseReport),
        displayText: " ",
      },
      {
        path: ":id",
        element: React.createElement(SupportCaseView),
        displayText: TranslationKeys.Common.Support,
      },
      {
        path: "CreateCase",
        element: React.createElement(CreateView),
        displayText: TranslationKeys.Common.Support,
      },
      {
        path: "Team",
        element: React.createElement(TeamPage),
        displayText: TranslationKeys.Common.MeetTheTeam,
      },
    ],
  },
  {
    path: "Supplier",
    displayText: TranslationKeys.Supplier.Supplier,
    backNavigationPath: "Reports/Suppliers",
    children: [
      {
        path: ":id/Settings",
        element: React.createElement(SupplierSettings),
        displayText: TranslationKeys.Common.Settings,
      },
      {
        path: ":id",
        element: React.createElement(SupplierProfile),
        displayText: TranslationKeys.Supplier.Supplier,
      },
    ],
  },
  {
    path: "QualifiedShareDividend",
    displayText: TranslationKeys.Common.K10Forms,
    backNavigationPath: "Reports/QualifiedShareDividend",
    children: [
      {
        path: ":id",
        element: React.createElement(QualifiedShareDividendView),
      },
    ],
  },
  {
    path: "PaymentMethod",
    displayText: TranslationKeys.Common.PaymentMethod,
    children: [
      {
        path: ":id",
        element: React.createElement(PaymentMethodProfile),
        displayText: TranslationKeys.Common.PaymentMethod,
      },
    ],
  },
  {
    path: "Employees",
    displayText: TranslationKeys.Common.Employees,
    disableBackNavigation: true,
    children: [
      {
        path: "Salaries",
        displayText: TranslationKeys.Company.Salaries,
        backNavigationPath: "Employees/Salaries",
        children: [
          {
            path: "Edit",
            element: React.createElement(EditSalaries),
            displayText: TranslationKeys.Salary.EditSalary,
          },
          {
            path: "",
            element: React.createElement(SalaryReport),
            displayText: "",
          },
          {
            path: ":id",
            element: React.createElement(SalaryDraftView),
          },
          {
            path: "Specifications",
            element: React.createElement(SalarySpecificationReport),
            displayText: TranslationKeys.Salary.SalarySpecifications,
          },
        ],
      },
      {
        path: "Deviations",
        element: React.createElement(SalaryDeviationReport),
        displayText: TranslationKeys.SalaryDeviation.Deviations,
      },
      {
        path: "WintDeviations",
        element: React.createElement(WintDeviationReport),
        displayText: TranslationKeys.SalaryDeviation.Deviations,
      },
      {
        path: "Commission",
        element: React.createElement(CommissionSalaryReport),
        displayText: TranslationKeys.Common.Commission,
      },
      {
        path: "BusinessUnits",
        element: React.createElement(BusinessUnitsReport),
        displayText: TranslationKeys.BusinessUnit.BusinessUnits,
      },
      {
        path: "GrossSalaries",
        element: React.createElement(GrossSalaryReport),
        displayText: TranslationKeys.Salary.Salaries,
      },
      {
        path: "Benefits",
        element: React.createElement(BenefitReport),
        displayText: TranslationKeys.Benefit.BenefitsTitle,
      },
      {
        path: "WintSalaries",
        element: React.createElement(WintSalaryReport),
        displayText: TranslationKeys.Company.Salaries,
      },
    ],
  },
  {
    path: "Settings",
    element: React.createElement(Settings),
    displayText: TranslationKeys.Common.Settings,
  },
  {
    path: "Login",
    element: React.createElement(Login),
    displayText: TranslationKeys.Common.Logout,
  },
  {
    path: "IncomingInvoice",
    displayText: TranslationKeys.IncomingInvoice.IncomingInvoices,
    backNavigationPath: "Reports/Expenses/IncomingInvoices",
    children: [
      {
        path: ":id",
        element: React.createElement(IncomingInvoiceView),
        displayText: TranslationKeys.IncomingInvoice.IncomingInvoices,
      },
    ],
  },
  {
    path: "Receipt",
    displayText: TranslationKeys.Receipt.Receipt,
    backNavigationPath: "Reports/Expenses/Receipts",
    children: [
      {
        path: ":id",
        element: React.createElement(ReceiptView),
        displayText: TranslationKeys.Receipt.Receipt,
      },
    ],
  },
  {
    path: "Customer",
    displayText: TranslationKeys.Invoice.Customer,
    backNavigationPath: "Reports/Customers",
    children: [
      {
        path: "Create",
        element: React.createElement(CreateCustomerView),
        displayText: TranslationKeys.Invoice.CreateClient,
      },
      {
        path: ":id/Settings",
        element: React.createElement(CustomerSettings),
        displayText: TranslationKeys.Customer.CustomerSettings,
      },
      {
        path: ":id",
        element: React.createElement(CustomerProfile),
        displayText: TranslationKeys.Common.Customer,
      },
    ],
  },
  {
    path: "Projects",
    displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
    backNavigationPath: "Reports/Projects",
    children: [
      {
        path: "Create",
        element: React.createElement(CreateTimeReportingProject),
        displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
      },
      {
        path: ":id/Settings",
        element: React.createElement(TimeReportingProjectSettings),
        displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
      },
      {
        path: ":id",
        element: React.createElement(TimeReportingProjectProfile),
        displayText: TranslationKeys.TimeReportingProject.TimeReportingProjects,
      },
    ],
  },
  {
    path: "Invoice",
    displayText: TranslationKeys.Common.Invoice,
    backNavigationPath: "Reports/Revenue/Invoices",
    children: [
      {
        path: "Create",
        element: React.createElement(CreateInvoiceContainer),
        displayText: TranslationKeys.Invoice.CreateInvoice,
      },
      {
        path: ":id",
        element: React.createElement(InvoiceView),
        displayText: TranslationKeys.Common.Invoice,
      },
    ],
  },
  {
    path: "RecurringInvoice",
    displayText: TranslationKeys.Invoice.RecurringInvoice,
    backNavigationPath: "Reports/Revenue/RecurringInvoices",
    children: [
      {
        path: ":id",
        element: React.createElement(RecurringInvoiceView),
        displayText: "Recurring invoice",
      },
    ],
  },
  {
    path: "Article",
    displayText: TranslationKeys.Common.Articles,
    backNavigationPath: "Reports/Articles",
    children: [
      {
        path: ":id/Settings",
        element: React.createElement(ArticleSettings),
        displayText: TranslationKeys.Common.Settings,
      },
      {
        path: ":id",
        element: React.createElement(ArticleProfileDashboard),
        displayText: TranslationKeys.Common.Articles,
      },
    ],
  },
  {
    path: "Employee",
    displayText: TranslationKeys.Common.Employee,
    disableBackNavigation: true,
    children: [
      {
        path: "Create",
        element: React.createElement(EmployeeSettings),
      },
      {
        path: ":id",
        element: React.createElement(EmployeeSettings),
      },
    ],
  },
  {
    path: "CashReconciliation",
    displayText: TranslationKeys.CashReconciliation.CashReconciliation,
    backNavigationPath: "Reports/Revenue/CashReconciliations",
    children: [
      {
        path: ":id",
        element: React.createElement(CashReconciliationView),
        displayText: TranslationKeys.CashReconciliation.CashReconciliation,
      },
    ],
  },
  {
    path: "RevenueReport",
    displayText: TranslationKeys.RevenueReporting.Reports,
    backNavigationPath: "Reports/Revenue/CashReconciliations",
    children: [
      {
        path: ":id",
        element: React.createElement(RevenueReportView),
        displayText: TranslationKeys.RevenueReporting.Reports,
      },
    ],
  },
  {
    path: "AccountingDocument",
    displayText: TranslationKeys.Document.GenericDocuments,
    backNavigationPath: "Reports/AccountingDocuments",
    children: [
      {
        path: ":id",
        element: React.createElement(GenericDocumentView),
        displayText: TranslationKeys.Document.GenericDocuments,
      },
    ],
  },
  {
    path: "FinancialStatement",
    displayText: TranslationKeys.AnnualAccount.AnnualAccounts,
    backNavigationPath: "Reports/FinancialStatements",
    children: [
      {
        path: ":id",
        element: React.createElement(FinancialStatementView),
        displayText: TranslationKeys.AnnualAccount.AnnualAccounts,
      },
      {
        path: ":id/:reportType",
        element: React.createElement(FinancialStatementView),
        displayText: TranslationKeys.AnnualAccount.AnnualAccounts,
      },
    ],
  },
  {
    path: "BusinessUnits",
    displayText: TranslationKeys.BusinessUnit.BusinessUnits,
    backNavigationPath: "Reports/BusinessUnits",
    children: [
      {
        path: "Create",
        element: React.createElement(CreateBusinessUnit),
        displayText: TranslationKeys.BusinessUnit.CreateBusinessUnit,
      },
      {
        path: ":id/Settings",
        element: React.createElement(BusinessUnitSettings),
        displayText: TranslationKeys.BusinessUnit.EditBusinessUnit,
      },
      {
        path: ":id",
        element: React.createElement(BusinessUnitProfile),
        displayText: "Need to add display text",
      },
    ],
  },
  {
    path: "Quotation",
    displayText: TranslationKeys.Common.Quotation,
    backNavigationPath: "Reports/Quotes",
    children: [
      {
        path: "Create",
        element: React.createElement(CreateQuotationContainer),
        displayText: TranslationKeys.Quotation.CreateQuotation,
      },
      {
        path: ":id",
        element: React.createElement(QuotationView),
        displayText: TranslationKeys.Common.Quotation,
      },
    ],
  },
  {
    path: "Voucher",
    displayText: TranslationKeys.Accounting.ManualPayment,
    backNavigationPath: "Reports/Accounting",
    children: [
      {
        path: ":id",
        element: React.createElement(VoucherView),
        displayText: TranslationKeys.Common.Accounting,
      },
    ],
  },
  {
    path: "Survey",
    displayText: TranslationKeys.Survey.Survey,
    children: [
      {
        path: ":id",
        element: React.createElement(SurveyView),
        displayText: TranslationKeys.Common.Submit2,
      },
    ],
  },
  {
    path: "TimeReport",
    displayText: TranslationKeys.TimeReporting.TimeReporting,
    children: [
      {
        path: "Weekly",
        element: React.createElement(TimeReportingView),
        displayText: TranslationKeys.TimeReporting.WeeklyTimeReport,
      },
      {
        path: "Monthly",
        element: React.createElement(TimeReportingMonthView),
        displayText: TranslationKeys.TimeReporting.MonthlyTimeReport,
      },
    ],
  },
  {
    path: "Todo",
    displayText: "",
    children: [
      {
        path: ":id",
        element: React.createElement(TodoView),
        displayText: TranslationKeys.Common.Todo,
      },
    ],
  },
  {
    path: "AdvisoryView",
    displayText: TranslationKeys.Reports.AdvisoryView,
    children: [
      {
        path: "ReconciliationReport",
        element: React.createElement(ReconciliationReportView),
        displayText: TranslationKeys.Accounting.Reconciliation,
      },
      {
        path: "DocumentationLog",
        element: React.createElement(DocumentationLogView),
        displayText: TranslationKeys.Advisor.DocumentationLog,
      },
      {
        path: "MonthlyReports",
        displayText: TranslationKeys.Advisor.MonthlyReports,
        // The breadcrumb builds a crumb's href from this route's own `path` segment alone, so a
        // route nested below the top level has to spell out its full path here or the link drops
        // its ancestors (`/MonthlyReports` instead of `/AdvisoryView/MonthlyReports`). Same reason
        // Quotation carries `Reports/Quotes`.
        backNavigationPath: "AdvisoryView/MonthlyReports",
        children: [
          {
            path: ":id",
            element: React.createElement(MonthlyReportDetailView),
            displayText: TranslationKeys.Advisor.MonthlyReports,
          },
          {
            path: "",
            element: React.createElement(MonthlyReportsList),
            displayText: "",
          },
        ],
      },
      {
        path: "VatReports",
        displayText: TranslationKeys.Advisor.VatReports,
        // Spelled out in full for the breadcrumb, same as MonthlyReports above.
        backNavigationPath: "AdvisoryView/VatReports",
        children: [
          {
            path: ":id",
            element: React.createElement(VatReportDetailView),
            displayText: TranslationKeys.Advisor.VatReports,
          },
          {
            path: "",
            element: React.createElement(VatReportsList),
            displayText: "",
          },
        ],
      },
      {
        path: "EmailAddresses",
        element: React.createElement(AdvisorEmailAddresses),
        displayText: TranslationKeys.Advisor.EmailAddresses,
      },
      {
        path: "",
        element: React.createElement(AdvisorDashboard),
        displayText: "",
      },
    ],
  },
  {
    path: "DocumentDraft",
    displayText: "",
    children: [
      {
        path: ":id",
        element: React.createElement(DocumentDraftView),
        displayText: TranslationKeys.DocumentDraft.ReceivedDocuments,
      },
    ],
  },
  {
    path: "SelfInvoice",
    displayText: TranslationKeys.Invoice.SelfInvoice,
    disableBackNavigation: true,
    children: [
      {
        path: ":id",
        element: React.createElement(CreateSelfInvoiceViewContainer),
        displayText: TranslationKeys.Invoice.CreateInvoice,
      },
    ],
  },
  {
    path: "MonthlyReports",
    displayText: TranslationKeys.MonthlyReport.Title,
    disableBackNavigation: true,
    children: [
      {
        path: ":id",
        element: React.createElement(MonthlyReportView),
        displayText: TranslationKeys.MonthlyReport.Title,
      },
    ],
  },
];
